// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/romeDay.ts
// 🔧 PURPOSE: Giorni e ore nel fuso degli appartamenti (Europe/Rome).
//             "Oggi", "il giorno dell'arrivo", "la sera" sono quelli di
//             Roma, non quelli del telefono: un ospite che apre l'app da
//             New York deve vedere le stesse cose di chi è già in casa.
//
//             Stavano dentro la chat (GuestChatDrawer); adesso li usa anche
//             MyStay per decidere cosa mostrare prima del giorno di arrivo.
// ============================================

export const ROME_TZ = 'Europe/Rome';

const ROME_DAY_KEY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: ROME_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** "2026-09-22": il giorno di Roma di una data, per confrontare i giorni. */
export function romeDayKey(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return ROME_DAY_KEY_FMT.format(d);
}

/**
 * Il giorno prima/dopo di una chiave "AAAA-MM-GG", sul calendario e non
 * "24 ore fa": con l'ora legale un giorno puo' durare 23 o 25 ore, e il
 * vecchio "ieri = 24h fa" sbagliava anche solo passata la mezzanotte.
 */
export function shiftDayKey(key: string, delta: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + delta));
  return d.toISOString().slice(0, 10);
}

/** Giorni di calendario da `b` ad `a` (chiavi "AAAA-MM-GG"); null se una manca. */
export function dayKeyDiff(a: string, b: string): number | null {
  const ma = /^(\d{4})-(\d{2})-(\d{2})$/.exec(a);
  const mb = /^(\d{4})-(\d{2})-(\d{2})$/.exec(b);
  if (!ma || !mb) return null;
  const ta = Date.UTC(Number(ma[1]), Number(ma[2]) - 1, Number(ma[3]));
  const tb = Date.UTC(Number(mb[1]), Number(mb[2]) - 1, Number(mb[3]));
  return Math.round((ta - tb) / 86400000);
}

/**
 * Quanti giorni di Roma mancano a `value` (ISO o "AAAA-MM-GG"): 0 il giorno
 * stesso, negativo dopo. Lo stesso conto alla rovescia della Home. Una data
 * salvata come mezzanotte italiana ("2026-10-17T22:00:00Z" = 18 ottobre)
 * resta il 18: contata in UTC diventava il 17, e la pagina del check-in
 * diceva un giorno in meno della Home. null se la data non si legge.
 */
export function romeDaysUntil(value: string | Date | null | undefined, now: Date = new Date()): number | null {
  if (!value) return null;
  const s = value instanceof Date ? '' : String(value).trim();
  const key = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : romeDayKey(value);
  const today = romeDayKey(now);
  if (!key || !today) return null;
  return dayKeyDiff(key, today);
}

const ROME_HOUR_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: ROME_TZ, hour: '2-digit', hour12: false });

/** L'ora di Roma (0-23): "la sera" e' la sera negli appartamenti, non sul telefono. */
export function romeHour(d: Date): number {
  const h = parseInt(ROME_HOUR_FMT.format(d), 10);
  // Qualche motore scrive "24" per la mezzanotte.
  return isNaN(h) ? d.getHours() : h % 24;
}

/**
 * true finché a Roma non è ancora arrivato il giorno del check-in (dalla
 * mezzanotte di quel giorno è false). Senza una data valida: false, cioè
 * "si comporta come sempre".
 */
export function beforeArrivalDay(checkIn: string | null | undefined, now: Date = new Date()): boolean {
  if (!checkIn) return false;
  const inKey = romeDayKey(checkIn);
  const today = romeDayKey(now);
  return !!inKey && !!today && today < inKey;
}

/**
 * true dal giorno DOPO la partenza (dalla mezzanotte di Roma): il soggiorno
 * e' finito. La sessione dura ancora quel giorno (recensione, "prenota di
 * nuovo"), ma la casa non si comanda piu': niente codice della porta, WiFi,
 * clima o lavatrice. Senza una data valida: false.
 */
export function afterCheckoutDay(checkOut: string | null | undefined, now: Date = new Date()): boolean {
  if (!checkOut) return false;
  const outKey = romeDayKey(checkOut);
  const today = romeDayKey(now);
  return !!outKey && !!today && today > outKey;
}

/**
 * Il margine dopo l'orario di check-out: lo stesso del channel manager
 * (STAY_CHECKOUT_GRACE_MS in lib/guest-home-network), che da li' in poi
 * rifiuta il codice della porta, il clima e la lavatrice.
 */
export const CHECKOUT_GRACE_MINUTES = 60;

const ROME_HM_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: ROME_TZ, hour: '2-digit', minute: '2-digit', hour12: false });

/** I minuti dalla mezzanotte di Roma (0-1439). */
export function romeMinutes(d: Date): number {
  const parts = ROME_HM_FMT.formatToParts(d);
  const h = parseInt(parts.find((p) => p.type === 'hour')?.value || '', 10);
  const m = parseInt(parts.find((p) => p.type === 'minute')?.value || '', 10);
  if (isNaN(h) || isNaN(m)) return d.getHours() * 60 + d.getMinutes();
  return (h % 24) * 60 + m;
}

/** "10:30" → 630. Un orario scritto male vale le 10:00, come per il channel manager. */
function checkOutMinutes(time: string | null | undefined): number {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(time ?? '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : 10 * 60;
}

/**
 * true quando il soggiorno e' FINITO per la casa: il giorno della partenza
 * oltre l'orario di check-out piu' un'ora (ora di Roma), e dal giorno dopo.
 * Da li' niente codice della porta, WiFi, clima o lavatrice, anche se la
 * sessione dura fino al giorno dopo (recensione, "prenota di nuovo").
 * `checkOutTime` e' l'orario vero: late check-out comprato, orario
 * concordato, 10:00. Senza una data valida: false (come prima).
 */
export function stayOverAt(
  checkOut: string | null | undefined,
  checkOutTime: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!checkOut) return false;
  const s = String(checkOut).trim();
  const outKey = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : romeDayKey(s);
  const today = romeDayKey(now);
  if (!outKey || !today) return false;
  if (today !== outKey) return today > outKey;
  return romeMinutes(now) > checkOutMinutes(checkOutTime) + CHECKOUT_GRACE_MINUTES;
}

/**
 * true dall'orario di check-out IN POI (compreso) il giorno della partenza, e
 * dal giorno dopo: il momento in cui il channel manager smette di vendere il
 * late check-out (late-checkout-smart: `now >= fromTime` il giorno D, motivo
 * `checkout_passed`). A differenza di stayOverAt non c'è l'ora di margine:
 * alle 10:00 la fascia sparisce, non alle 11:00. Senza una data valida:
 * false (come prima).
 */
export function checkoutTimeReached(
  checkOut: string | null | undefined,
  checkOutTime: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!checkOut) return false;
  const s = String(checkOut).trim();
  const outKey = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : romeDayKey(s);
  const today = romeDayKey(now);
  if (!outKey || !today) return false;
  if (today !== outKey) return today > outKey;
  return romeMinutes(now) >= checkOutMinutes(checkOutTime);
}
